import { createContext, useContext, useEffect, useState } from 'react'

type Theme = 'dark' | 'light' | 'system'

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'vite-ui-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, _setTheme] = useState<Theme>('light')
    // () =>
    //   (localStorage.getItem(storageKey) as Theme) ||
    //   (window.matchMedia('(prefers-color-scheme: dark)').matches
    //     ? 'dark'
    //     : 'light')
  // )

  useEffect(() => {
    const root = window.document.documentElement
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

    const applyTheme = (theme: Theme) => {
      const systemTheme = mediaQuery.matches ? 'dark' : 'light'
      const effectiveTheme = theme === 'system' ? systemTheme : theme

      // 使用 requestAnimationFrame 优化渲染
      requestAnimationFrame(() => {
        // 避免不必要的 DOM 操作
        if (root.classList.contains(effectiveTheme)) return

        const classList = root.classList

        // 构造最终的 className
        const otherClasses = Array.from(classList).filter(
          (cls) => cls !== 'dark' && cls !== 'light'
        )
        const newClassName = [...otherClasses, effectiveTheme].join(' ')
        root.className = newClassName
      })
    }

    const handleChange = () => {
      if (theme === 'system') {
        applyTheme('system')
      }
    }

    applyTheme(theme)
    mediaQuery.addEventListener('change', handleChange)

    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  const setTheme = (theme: Theme) => {
    // 使用 requestAnimationFrame 优化状态更新
    requestAnimationFrame(() => {
      localStorage.setItem(storageKey, theme)
      _setTheme(theme)
    })
  }

  const value = {
    theme,
    setTheme,
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider')

  return context
}
